import React from "react";
import { Link } from "wouter";
import { Layout } from "@/components/layout";
import { CourseCard } from "@/components/course-card";
import { Button } from "@/components/ui/button";
import { useListCourses, useGetStatsOverview, useListUpcomingSessions } from "@workspace/api-client-react";
import { Terminal, Sparkles, Cpu, ArrowUpRight, Calendar, Quote } from "lucide-react";
import { dateMediumFmt } from "@/lib/format";
import { motion } from "framer-motion";
import { AnimatedSection } from "@/components/animated-section";
import { fadeUp, stagger } from "@/lib/motion";

const TESTIMONIALS = [
  {
    quote: "I built a chatbot that helps me with my homework. It's literally my favorite thing I've ever made.",
    name: "Maya, 14",
    detail: "Vibecoding cohort · Spring '26",
  },
  {
    quote: "I came in only knowing Scratch. Eight weeks later I was basically a Python Jedi. The pace is real.",
    name: "Andre, 16",
    detail: "Python Foundations · Live",
  },
  {
    quote: "My instructor treated me like a real engineer. That changed how I think about myself.",
    name: "Jin, 13",
    detail: "AI Foundations · Self-paced",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Pick your path",
    body: "Three tracks — Python, AI, or Vibecoding. Choose live cohorts for accountability, or self-paced for flexibility.",
  },
  {
    n: "02",
    title: "Build, weekly",
    body: "Real projects from week one. Every lesson ends with something you actually shipped — not a worksheet.",
  },
  {
    n: "03",
    title: "Get unstuck fast",
    body: "Working engineers as instructors, hands-on feedback on your code, and a private cohort channel.",
  },
];

export default function Home() {
  const { data: courses } = useListCourses();
  const { data: stats } = useGetStatsOverview();
  const { data: sessions } = useListUpcomingSessions({ limit: 4 });
  const featuredCourses = courses?.slice(0, 3) || [];

  return (
    <Layout>
      {/* HERO ───────────────────────────────────────── */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" aria-hidden="true" />
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-secondary/40 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute top-40 -left-32 w-[20rem] h-[20rem] rounded-full bg-primary/20 blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="container mx-auto px-4 pt-20 md:pt-28 pb-20 md:pb-32 relative z-10">
          <motion.div
            variants={stagger(0.09)}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end"
          >
            <div className="lg:col-span-8">
              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <span className="inline-flex items-center gap-2 bg-foreground text-background px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" aria-hidden="true" />
                  Vol. 01 / Issue Fall '26
                </span>
                <span className="hidden sm:inline">// for ages 11–18</span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-display font-bold text-[3.25rem] sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-[-0.035em] mb-8 text-balance"
              >
                Code your{" "}
                <span className="font-italic-serif font-normal text-primary">future.</span>
                <br />
                <span className="text-marker">Vibe</span> with AI.
              </motion.h1>

              <motion.p variants={fadeUp} className="text-xl md:text-2xl text-muted-foreground max-w-2xl mb-10 leading-relaxed">
                MainLoop is the after-school code lab where curious teenagers learn{" "}
                <span className="text-foreground font-medium">Python, AI, and Vibecoding</span> —
                taught by working engineers, in real production tools.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="h-14 px-8 rounded-full text-base font-bold">
                  <Link href="/courses">
                    Browse the catalog
                    <ArrowUpRight className="ml-1 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-14 px-8 rounded-full text-base font-bold bg-background">
                  <Link href="/about">How it works</Link>
                </Button>
              </motion.div>
            </div>

            {/* Terminal mock */}
            <motion.div
              variants={fadeUp}
              className="lg:col-span-4"
            >
              <div className="bg-foreground rounded-xl shadow-2xl border border-foreground/30 overflow-hidden font-mono text-[12px] sm:text-[13px] leading-relaxed">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-background/10 bg-background/[0.04]">
                  <span className="w-2.5 h-2.5 rounded-full bg-accent" />
                  <span className="w-2.5 h-2.5 rounded-full bg-secondary" />
                  <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                  <span className="ml-2 text-background/50 text-xs">~/mainloop/lesson-01.py</span>
                </div>
                <div className="p-4 sm:p-5 text-background/90 space-y-1 overflow-x-auto">
                  <div><span className="text-background/40">$</span> <span className="text-secondary">python</span> lesson_01.py</div>
                  <div className="text-background/40"># your first AI tutor</div>
                  <div><span className="text-accent">from</span> <span className="text-secondary">openai</span> <span className="text-accent">import</span> OpenAI</div>
                  <div className="h-2" />
                  <div><span className="text-primary">def</span> <span className="text-secondary">ask</span>(q):</div>
                  <div className="pl-4">return llm.respond(q)</div>
                  <div className="h-2" />
                  <div className="text-background/60">&gt;&gt;&gt; ask(<span className="text-accent">"why does the moon</span></div>
                  <div className="text-background/60 pl-4"><span className="text-accent">change shape?"</span>)</div>
                  <div className="text-secondary cursor-blink">it doesn&rsquo;t — your view does</div>
                </div>
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-center">
                fig. 01 · week one project
              </div>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* FEATURED COURSES ─────────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="section-tag mb-4">[ 02 ] · Featured paths</div>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
                Start with a <span className="font-italic-serif text-primary">cohort</span>,
                or learn at your own pace.
              </h2>
            </div>
            <Link href="/courses" className="font-mono text-sm uppercase tracking-widest inline-flex items-center gap-2 hover:text-primary transition-colors group/link self-start md:self-end">
              View all courses
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
            </Link>
          </div>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerChildren={0.1}>
            {featuredCourses.map(course => (
              <CourseCard key={course.id} course={course} />
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* HOW IT WORKS ─────────────────────────────── */}
      <section className="py-24 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-sm opacity-[0.06] pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mb-16">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">[ 03 ] · The MainLoop method</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
              Three steps from <span className="font-italic-serif text-secondary">curious</span> to capable.
            </h2>
          </div>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-px bg-background/15 border border-background/15" staggerChildren={0.12}>
            {PROCESS.map(step => (
              <motion.div
                key={step.n}
                variants={fadeUp}
                className="bg-foreground p-8 md:p-10 relative"
              >
                <div className="flex items-baseline justify-between mb-8">
                  <span className="font-display text-7xl md:text-8xl font-bold text-secondary leading-none">{step.n}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-background/40">step</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold mb-4 tracking-tight">{step.title}</h3>
                <p className="text-background/70 leading-relaxed">{step.body}</p>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* TOPICS ────────────────────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="section-tag mb-4">[ 04 ] · Topics</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
              We teach the <span className="font-italic-serif">tools real builders use today.</span>
            </h2>
          </div>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border" staggerChildren={0.1}>
            {[
              { icon: Terminal, num: "P—01", title: "Python", body: "The language of AI. From your first print() to deployed Flask apps in eight weeks.", link: "/courses?topic=python", chipBg: "bg-primary/10 text-primary" },
              { icon: Sparkles, num: "V—02", title: "Vibecoding", body: "Pair-program with AI. Use Cursor, Copilot, and LLMs to ship web apps at lightspeed.", link: "/courses?topic=vibecoding", chipBg: "bg-secondary/30 text-foreground" },
              { icon: Cpu, num: "A—03", title: "AI Foundations", body: "How neural nets work, prompt engineering, and building real tools — not magic boxes.", link: "/courses?topic=ai", chipBg: "bg-accent/15 text-accent" },
            ].map(topic => {
              const Icon = topic.icon;
              return (
                <motion.div
                  key={topic.title}
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 280, damping: 22 }}
                  className="bg-background p-8 md:p-10 group/topic"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className={`w-14 h-14 ${topic.chipBg} rounded-md flex items-center justify-center`}>
                      <Icon className="w-7 h-7" aria-hidden="true" />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{topic.num}</span>
                  </div>
                  <h3 className="font-display text-3xl font-bold mb-3 tracking-tight">{topic.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{topic.body}</p>
                  <Link href={topic.link} className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-foreground border-b border-foreground hover:text-primary hover:border-primary transition-colors">
                    See courses
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/topic:translate-x-0.5 group-hover/topic:-translate-y-0.5" />
                  </Link>
                </motion.div>
              );
            })}
          </AnimatedSection>
        </div>
      </section>

      {/* TESTIMONIALS ─────────────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border bg-secondary/15">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="section-tag mb-4">[ 05 ] · From our students</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
              Words from the <span className="font-italic-serif">build log.</span>
            </h2>
          </div>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerChildren={0.12}>
            {TESTIMONIALS.map(t => (
              <motion.figure
                key={t.name}
                variants={fadeUp}
                className="bg-background border border-border rounded-xl p-8 flex flex-col h-full shadow-sm"
              >
                <Quote className="w-7 h-7 text-primary mb-6 shrink-0" aria-hidden="true" />
                <blockquote className="font-display text-xl md:text-2xl font-medium leading-snug tracking-tight mb-6 text-balance flex-1">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="border-t border-border pt-4">
                  <div className="font-bold">{t.name}</div>
                  <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground mt-1">{t.detail}</div>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* UPCOMING SESSIONS ────────────────────────── */}
      {sessions && sessions.length > 0 && (
        <section className="py-24 md:py-32 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="max-w-2xl">
                <div className="section-tag mb-4">[ 06 ] · This week's live sessions</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-balance">
                  See what active cohorts are <span className="font-italic-serif text-primary">building right now.</span>
                </h2>
              </div>
            </div>

            <AnimatedSection className="divide-y divide-border border-y border-border" staggerChildren={0.06}>
              {sessions.map(session => (
                <motion.div
                  variants={fadeUp}
                  key={session.lessonId}
                  className="grid grid-cols-12 gap-4 py-6 items-center group/row hover:bg-secondary/15 transition-colors px-2"
                >
                  <div className="col-span-12 md:col-span-3 font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                    {dateMediumFmt.format(new Date(session.zoomScheduledAt))}
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <div className="font-display text-lg md:text-xl font-bold tracking-tight">
                      {session.lessonTitle}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      Lesson {session.lessonOrderIndex} ·{" "}
                      <Link href={`/courses/${session.courseSlug}`} className="underline hover:text-primary">
                        {session.courseTitle}
                      </Link>
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-2 md:text-right">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest bg-foreground text-background rounded-full px-3 py-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" aria-hidden="true" />
                      Live
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* CTA ──────────────────────────────────────── */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="section-tag mb-6">[ * * * ]</div>
            <h2 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight mb-8 text-balance">
              Ready to <span className="font-italic-serif text-primary">build</span> something
              <br />you&rsquo;re actually proud of?
            </h2>
            <p className="text-xl text-muted-foreground max-w-xl mx-auto mb-10">
              Browse the catalog and pick a path. Cohorts open every season.
            </p>
            <Button asChild size="lg" className="h-14 px-10 rounded-full text-base font-bold">
              <Link href="/courses">
                Find your course
                <ArrowUpRight className="ml-1 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
